import styled, { useTheme } from 'styled-components';
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { label, monthTick, projectDate } from '@entities/ksp/format';
import type { KspCurve } from '@entities/ksp/types';
import { Card, Muted, Row, Small, Table, TableBox } from '../styles';
import { curvePoints } from './pulse';

// План — синий, факт и прогноз — фирменная терракота (прогноз пунктиром). Пара проверена
// на различимость при дальтонизме в обеих темах (validate_palette, ΔE ≥ 22).
const PLAN = { dark: '#3987e5', light: '#2a78d6' };

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin: 4px 0 8px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.text.secondary};
`;
const Swatch = styled.i<{ $color: string; $dashed?: boolean }>`
  display: inline-block;
  width: 18px;
  margin-right: 6px;
  vertical-align: middle;
  border-top: 2px ${({ $dashed }) => ($dashed ? 'dashed' : 'solid')} ${({ $color }) => $color};
`;
const Details = styled.details`
  margin-top: 6px;
  summary {
    cursor: pointer;
    font-size: 12px;
    color: ${({ theme }) => theme.colors.text.muted};
  }
`;

const pct = (v: number | null | undefined) =>
  v == null ? '—' : `${v.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} %`;

/** Накопительная кривая план / факт / прогноз за весь проект. */
export function PlanFactCurve({
  curves,
  contractFinish,
  timezone,
  reason,
}: {
  curves: KspCurve[] | undefined;
  contractFinish: string | null;
  timezone: string;
  reason: string | null;
}) {
  const theme = useTheme();
  const curve = curves?.[0];
  const rows = curve ? curvePoints(curve.points) : [];
  const data = rows
    .map((row) => ({ ...row, t: Date.parse(row.date) }))
    .filter((row) => Number.isFinite(row.t));
  const hasValues = data.some((row) => row.plan != null || row.fact != null || row.forecast != null);
  const plan = theme.name === 'dark' ? PLAN.dark : PLAN.light;
  const fact = theme.colors.primary;
  const now = Date.now();
  const contract = contractFinish ? Date.parse(contractFinish) : NaN;
  const minT = data.length ? data[0].t : now;
  const maxT = data.length ? data[data.length - 1].t : now;
  const domain: [number, number] = [
    Math.min(minT, now),
    Math.max(maxT, Number.isFinite(contract) ? contract : maxT, now),
  ];
  return (
    <Card>
      <Row>
        <h3>Накопительная кривая</h3>
        {curve && (
          <Small>
            {label(curve.method)}, {curve.unit === '%' ? '% от объёма проекта' : curve.unit}
          </Small>
        )}
      </Row>
      {!hasValues ? (
        <Muted>
          Кривой пока нет{reason ? `: ${reason.charAt(0).toLowerCase()}${reason.slice(1)}` : ''}.
          Она появится, когда будут утверждены базовый график и веса работ.
        </Muted>
      ) : (
        <>
          <Legend>
            <span>
              <Swatch $color={plan} />
              План
            </span>
            <span>
              <Swatch $color={fact} />
              Факт
            </span>
            <span>
              <Swatch $color={fact} $dashed />
              Прогноз
            </span>
          </Legend>
          <ResponsiveContainer width="100%" height={260}>
            <ComposedChart data={data} margin={{ top: 8, right: 16, bottom: 0, left: -12 }}>
              <CartesianGrid stroke={theme.colors.border} vertical={false} />
              <XAxis
                dataKey="t"
                type="number"
                scale="time"
                domain={domain}
                tickFormatter={(t: number) => monthTick(new Date(t).toISOString(), timezone)}
                stroke={theme.colors.text.muted}
                tick={{ fontSize: 11 }}
                tickLine={false}
              />
              <YAxis
                domain={[0, 100]}
                tickFormatter={(v: number) => `${v}%`}
                stroke={theme.colors.text.muted}
                tick={{ fontSize: 11 }}
                tickLine={false}
                axisLine={false}
              />
              <Tooltip
                labelFormatter={(t) => projectDate(new Date(Number(t)).toISOString(), timezone)}
                formatter={(value, name) => [pct(value as number | null), String(name)]}
                contentStyle={{
                  background: theme.colors.backgroundElevated,
                  border: `1px solid ${theme.colors.borderStrong}`,
                  borderRadius: 8,
                  fontSize: 12,
                }}
              />
              <ReferenceLine
                x={now}
                stroke={theme.colors.text.secondary}
                strokeDasharray="3 3"
                label={{ value: 'сегодня', position: 'insideTopLeft', fontSize: 11, fill: theme.colors.text.secondary }}
              />
              {Number.isFinite(contract) && (
                <ReferenceLine
                  x={contract}
                  stroke={theme.colors.text.secondary}
                  label={{ value: 'договор', position: 'insideBottomRight', fontSize: 11, fill: theme.colors.text.secondary }}
                />
              )}
              <Line
                dataKey="plan"
                name="План"
                stroke={plan}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                dataKey="forecast"
                name="Прогноз"
                stroke={fact}
                strokeWidth={2}
                strokeDasharray="6 4"
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
              <Line
                dataKey="fact"
                name="Факт"
                stroke={fact}
                strokeWidth={2.5}
                dot={false}
                connectNulls={false}
                isAnimationActive={false}
              />
            </ComposedChart>
          </ResponsiveContainer>
          <Details>
            <summary>Таблица значений</summary>
            <TableBox>
              <Table>
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>План</th>
                    <th>Факт</th>
                    <th>Прогноз</th>
                  </tr>
                </thead>
                <tbody>
                  {data.map((row) => (
                    <tr key={row.date}>
                      <td>{projectDate(row.date, timezone)}</td>
                      <td>{pct(row.plan)}</td>
                      <td>{pct(row.fact)}</td>
                      <td>{pct(row.forecast)}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </TableBox>
          </Details>
        </>
      )}
    </Card>
  );
}
